import type {
  ApiErrorBody,
  AppointmentDetail,
  AppointmentSummary,
  AuditListQuery,
  AuditListResult,
  AvailableCalendar,
  BusyCalendarEntry,
  CalendarConfig,
  DocumentViewUrl,
  DriftParams,
  DriftResult,
  GoogleConnectionStatus,
  SnapshotParams,
  TroubleshootSnapshot,
} from "./types.js";

export class ApiError extends Error {
  constructor(
    public code: string,
    public status: number,
    message?: string,
  ) {
    super(message ?? code);
  }
}

export type GetToken = () => Promise<string>;

export type ListQuery = { from: string; to: string; status?: string };

export type CalendarConfigInput = Omit<
  CalendarConfig,
  "updated_by" | "updated_at"
>;

export type AdminApi = {
  listAppointments(q: ListQuery): Promise<AppointmentSummary[]>;
  getAppointment(id: string): Promise<AppointmentDetail>;
  cancelAppointment(id: string, reason: string): Promise<void>;
  getDocumentViewUrl(id: string): Promise<DocumentViewUrl>;
  getCalendarConfig(): Promise<CalendarConfig | null>;
  putCalendarConfig(input: CalendarConfigInput): Promise<CalendarConfig>;
  // Audit log — keyset-paginated by occurred_at desc.
  listAuditLog(q: AuditListQuery): Promise<AuditListResult>;
  // Google Calendar OAuth connect/disconnect flow. Calendar selection is
  // a separate step after connect.
  getGoogleStatus(): Promise<GoogleConnectionStatus>;
  startGoogleOAuth(): Promise<{ url: string }>;
  disconnectGoogle(): Promise<void>;
  // Multi-calendar selection: enumerate the user's calendars, then save
  // the practice's chosen busy-source list.
  listAvailableCalendars(): Promise<{ calendars: AvailableCalendar[] }>;
  getBusyCalendars(): Promise<{ calendars: BusyCalendarEntry[] }>;
  putBusyCalendars(
    calendarIds: string[],
  ): Promise<{ calendars: BusyCalendarEntry[] }>;
  getTroubleshootSnapshot(params: SnapshotParams): Promise<TroubleshootSnapshot>;
  runTroubleshootDrift(params: DriftParams): Promise<DriftResult>;
};

export function buildAdminApi(baseUrl: string, getToken: GetToken): AdminApi {
  async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = await getToken();
    const headers = new Headers(init.headers);
    headers.set("authorization", `Bearer ${token}`);
    if (init.body && !headers.has("content-type")) {
      headers.set("content-type", "application/json");
    }

    let res: Response;
    try {
      res = await fetch(`${baseUrl}${path}`, { ...init, headers });
    } catch (cause) {
      throw new ApiError("network_error", 0, (cause as Error).message);
    }

    const text = await res.text();
    const body = text ? (JSON.parse(text) as unknown) : null;

    if (!res.ok) {
      const err = body as ApiErrorBody | null;
      const code = err?.error?.code ?? "unknown_error";
      throw new ApiError(code, res.status, err?.error?.message);
    }
    return body as T;
  }

  return {
    async listAppointments(q) {
      const params = new URLSearchParams({ from: q.from, to: q.to });
      if (q.status) params.set("status", q.status);
      return request<AppointmentSummary[]>(
        `/admin/api/appointments?${params.toString()}`,
      );
    },
    async getAppointment(id) {
      return request<AppointmentDetail>(`/admin/api/appointments/${id}`);
    },
    async cancelAppointment(id, reason) {
      await request<unknown>(`/admin/api/appointments/${id}/cancel`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      });
    },
    async getDocumentViewUrl(id) {
      return request<DocumentViewUrl>(
        `/admin/api/documents/${id}/view-url`,
      );
    },
    async getCalendarConfig() {
      try {
        return await request<CalendarConfig>(`/admin/api/calendar-config`);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    async putCalendarConfig(input) {
      return request<CalendarConfig>(`/admin/api/calendar-config`, {
        method: "PUT",
        body: JSON.stringify(input),
      });
    },
    async listAuditLog(q) {
      const params = new URLSearchParams();
      if (q.before) params.set("before", q.before);
      if (q.limit !== undefined) params.set("limit", String(q.limit));
      if (q.action) params.set("action", q.action);
      if (q.actor) params.set("actor", q.actor);
      const qs = params.toString();
      return request<AuditListResult>(
        `/admin/api/audit-log${qs ? `?${qs}` : ""}`,
      );
    },
    async getGoogleStatus() {
      return request<GoogleConnectionStatus>(
        `/admin/api/calendar/google/status`,
      );
    },
    async startGoogleOAuth() {
      return request<{ url: string }>(
        `/admin/api/calendar/google/authorize`,
        { method: "POST" },
      );
    },
    async disconnectGoogle() {
      await request<unknown>(`/admin/api/calendar/google/connection`, {
        method: "DELETE",
      });
    },
    async listAvailableCalendars() {
      return request<{ calendars: AvailableCalendar[] }>(
        `/admin/api/calendar/google/available-calendars`,
      );
    },
    async getBusyCalendars() {
      return request<{ calendars: BusyCalendarEntry[] }>(
        `/admin/api/calendar/busy-calendars`,
      );
    },
    async putBusyCalendars(calendarIds) {
      return request<{ calendars: BusyCalendarEntry[] }>(
        `/admin/api/calendar/busy-calendars`,
        {
          method: "PUT",
          body: JSON.stringify({ calendar_ids: calendarIds }),
        },
      );
    },
    async getTroubleshootSnapshot(params) {
      const qs = new URLSearchParams({
        fromDate: params.fromDate,
        toDate: params.toDate,
        durationMinutes: String(params.durationMinutes),
      });
      return request<TroubleshootSnapshot>(
        `/admin/api/troubleshoot/snapshot?${qs.toString()}`,
      );
    },
    async runTroubleshootDrift(params) {
      const qs = new URLSearchParams({
        fromDate: params.fromDate,
        toDate: params.toDate,
      });
      return request<DriftResult>(
        `/admin/api/troubleshoot/drift?${qs.toString()}`,
        { method: "POST" },
      );
    },
  };
}
